import { DOMParser as SchemaParser, DOMSerializer, type Node as ProseNode, type Slice } from 'prosemirror-model';
import { sanitizeEntryHtmlToFragment } from '../security/sanitize.ts';
import { entrySchema } from './schema.ts';

/**
 * Entry HTML in and out of the editor (docs/ENTRY-EDITOR-PLAN.md section 4.1). Trusted
 * Types allows no HTML strings except through DOMPurify, so HTML is always cleaned into
 * a DOM fragment first and ProseMirror parses that fragment, never a string. Writing
 * builds DOM nodes and reads `innerHTML`, which is not a Trusted Types sink.
 */

const parser = SchemaParser.fromSchema(entrySchema);
const serializer = DOMSerializer.fromSchema(entrySchema);

export function docFromHtml(html: string): ProseNode {
  return parser.parse(cleaned(html));
}

/** Pasted or dropped HTML, cleaned, as a slice to insert. */
export function sliceFromHtml(html: string): Slice {
  return parser.parseSlice(cleaned(html));
}

function cleaned(html: string): DocumentFragment {
  const fragment = sanitizeEntryHtmlToFragment(html);
  fontTagsToStyles(fragment);
  return fragment;
}

/** The CSS sizes for `<font size="1">` to `"7"`, as browsers map them. */
const FONT_TAG_SIZES = ['x-small', 'small', 'medium', 'large', 'x-large', 'xx-large', 'xxx-large'];

/**
 * WebKit's editing commands write `<font face size color>`, often with several of them on
 * one tag. A schema parse rule matches a tag once, so each `<font>` becomes a `<span>`
 * with the same font family, size and colour as styles, which the schema's style rules
 * then read together. The schema checks each value.
 */
function fontTagsToStyles(root: DocumentFragment): void {
  for (const font of [...root.querySelectorAll('font')]) {
    const span = font.ownerDocument.createElement('span');
    const face = font.getAttribute('face');
    const size = FONT_TAG_SIZES[Number(font.getAttribute('size')) - 1];
    const color = font.getAttribute('color');
    if (face) span.style.fontFamily = face;
    if (size) span.style.fontSize = size;
    if (color) span.style.color = color;
    span.append(...font.childNodes);
    font.replaceWith(span);
  }
}

/** The elements the schema reads (schema.ts); anything else would be lost by editing. */
const UNDERSTOOD = new Set([
  'P', 'DIV', 'H1', 'H2', 'H3', 'H4', 'H5', 'H6', 'BLOCKQUOTE', 'HR', 'UL', 'OL', 'LI', 'BR',
  'A', 'B', 'STRONG', 'I', 'EM', 'U', 'S', 'STRIKE', 'DEL', 'SPAN', 'FONT',
]);

const DESCRIBED: Record<string, string> = {
  TABLE: 'a table', TBODY: 'a table', THEAD: 'a table', TFOOT: 'a table', TR: 'a table', TD: 'a table', TH: 'a table',
  CAPTION: 'a table', COL: 'a table', COLGROUP: 'a table',
  IMG: 'an image in the text',
  PRE: 'preformatted text', CODE: 'code',
  SUB: 'subscript or superscript', SUP: 'subscript or superscript',
  DL: 'a definition list', DT: 'a definition list', DD: 'a definition list',
};

/**
 * What in an entry's HTML the editor cannot represent, described for the notice: the
 * entry opens read-only instead of losing it (docs/ENTRY-EDITOR-PLAN.md section 4.2).
 * Empty when it can be edited. Only what the read-only view shows counts: the sanitizer
 * has already dropped scripts and the like, which no view keeps.
 */
export function editProblems(html: string): string[] {
  const fragment = cleaned(html);
  const problems = new Set<string>();
  for (const element of fragment.querySelectorAll('*')) {
    if (!UNDERSTOOD.has(element.tagName)) problems.add(DESCRIBED[element.tagName] ?? 'formatting the web editor does not know yet');
  }
  if (problems.size === 0) {
    const shown = (fragment.textContent ?? '').replace(/\s+/g, '');
    const doc = parser.parse(fragment);
    if (doc.textBetween(0, doc.content.size, '', '').replace(/\s+/g, '') !== shown) problems.add('text the web editor would lose');
  }
  return [...problems];
}

/** The HTML to store: a body fragment in the schema's one form, with no `<html>` wrapper. */
export function htmlFromDoc(doc: ProseNode): string {
  const container = document.createElement('div');
  container.appendChild(serializer.serializeFragment(doc.content));
  return container.innerHTML;
}
